import React from "react";
import { Target, Users, Globe, Zap } from "lucide-react";

const AboutSection = ({ isVisible }) => {
  const values = [
    {
      icon: Target,
      title: "Purpose-Driven",
      description: "Every decision we make is guided by our mission to empower communities.",
    },
    {
      icon: Users,
      title: "Community First",
      description: "We believe in the power of collective action and mutual support.",
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Our reach extends across borders, touching lives worldwide.",
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "We leverage technology and creativity to solve real-world problems.",
    },
  ];

  return (
    <section
      id="about"
      className={`section-fade py-24 sm:py-32 bg-white ${isVisible ? "visible" : ""}`}
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 sm:mb-20">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/70 mb-6">
            <span className="w-8 h-px bg-[#D4AF37]" />
            About Us
            <span className="w-8 h-px bg-[#D4AF37]" />
          </span>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0A192F] tracking-tight font-['Outfit'] mb-6">
            A Family of Companies
            <span className="block text-[#D4AF37]">United by Purpose</span>
          </h2>
          <p className="text-lg text-[#475569] leading-relaxed">
            Prestigious Companies is the parent organization behind nine specialized companies, 
            each focused on solving unique challenges while working together to create lasting 
            impact for consumers, businesses, and communities. From charitable initiatives and 
            travel experiences to financial technology, marketing innovation, and AI-powered solutions — 
            each division shares a single goal.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="relative bg-[#F8FAFC] rounded-3xl p-10 sm:p-16 mb-16 sm:mb-20 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0A192F]/5 rounded-full blur-2xl" />
          
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <blockquote className="text-2xl sm:text-3xl font-light text-[#0A192F] font-['Outfit'] leading-relaxed">
              "We're here to make a{" "}
              <span className="font-medium text-[#D4AF37]">meaningful difference</span>{" "}
              in people's lives."
            </blockquote>
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-sm font-medium text-[#475569] tracking-wide uppercase">
                Our Mission
              </span>
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {values.map((value, index) => (
            <div
              key={value.title}
              className={`group p-8 rounded-2xl bg-white border border-slate-100 hover:border-[#0A192F]/20 transition-all duration-300 hover:shadow-lg animate-fade-in-up opacity-0 stagger-${index + 1}`}
              data-testid={`value-${value.title.toLowerCase().replace(" ", "-")}`}
            >
              <div className="w-14 h-14 bg-[#0A192F] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors duration-300">
                <value.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-medium text-[#0A192F] font-['Outfit'] mb-3">
                {value.title}
              </h3>
              <p className="text-[#475569] leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
